import { useCallback, useEffect, useRef } from 'react';
import { type SerializedStyles } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { useOnboardingShallowStore } from '@frontend/product-onboarding';
import { FeatureId, useEligibility, usePreference, type EligibilityCriteria } from '@frontend/user-preferences';
import { CI_BASE_FEATURE_IDS } from '../../constant';
import { useOnboardingEligibility } from '../../hooks/use-onboarding-eligibility';
import { CI_BASE_ONBOARDING_EVENTS, trackBaseOnboardingEvent } from '../../tracking';
import { hasReachedViewLimit } from '../../utils/banner.utils';
import { getCurrentTimestamp } from '../../utils/onboarding-lifecycle';
import { AnnouncementBannerUI } from './announcement-banner-ui';

const BASE_BANNER_CRITERIA: EligibilityCriteria = {
  strategy: 'any',
  conditions: [
    { type: 'view-count', maxViews: 4 },
    { type: 'click-count', maxClicks: 1 },
  ],
};

interface BaseAnnouncementBannerProps {
  containerStyles?: SerializedStyles;
}

type BannerActionSource = 'banner-click' | 'banner-dismiss' | 'banner-auto-dismiss';

export function BaseAnnouncementBanner({ containerStyles }: BaseAnnouncementBannerProps) {
  const { isEligible, isLoading } = useOnboardingEligibility();

  if (isLoading || !isEligible) {
    return null;
  }

  return <BaseAnnouncementBannerContent containerStyles={containerStyles} />;
}

function BaseAnnouncementBannerContent({ containerStyles }: BaseAnnouncementBannerProps) {
  const { t } = useTranslation('call-intelligence');
  const autoOpenHandled = useRef(false);
  const bannerSeenTracked = useRef(false);

  const { isGuideOpen, toggleOnboardingGuide } = useOnboardingShallowStore('isGuideOpen', 'toggleOnboardingGuide');

  const [nudgeLifecycle, setNudgeLifecycle] = usePreference('CIBaseChecklistNudgeLifecycle');

  const { shouldShow, disableEligibility, recordClick, eligibilityData } = useEligibility({
    featureId: CI_BASE_FEATURE_IDS.announcementBanner as FeatureId,
    defaultCriteria: BASE_BANNER_CRITERIA,
  });

  const openGuideFromAnnouncement = useCallback(
    (source: BannerActionSource) => {
      /**
       * This marks that the checklist opened once from the announcement flow.
       */
      if (!nudgeLifecycle.initialAutoOpenedAt) {
        setNudgeLifecycle({
          ...nudgeLifecycle,
          initialAutoOpenedAt: getCurrentTimestamp(),
        });
      }

      trackBaseOnboardingEvent(CI_BASE_ONBOARDING_EVENTS.checklistAutoOpened, {
        source,
      });

      if (!isGuideOpen) {
        toggleOnboardingGuide();
      }
    },
    [nudgeLifecycle, setNudgeLifecycle, isGuideOpen, toggleOnboardingGuide]
  );

  useEffect(() => {
    if (!shouldShow) return;
    if (bannerSeenTracked.current) return;

    bannerSeenTracked.current = true;

    trackBaseOnboardingEvent(CI_BASE_ONBOARDING_EVENTS.bannerSeen);
  }, [shouldShow]);

  useEffect(() => {
    if (autoOpenHandled.current) return;
    if (eligibilityData?.disable) return;

    const hasReachedAutoOpenLimit = hasReachedViewLimit(eligibilityData?.criteria);

    if (!hasReachedAutoOpenLimit) return;

    autoOpenHandled.current = true;

    disableEligibility();
    openGuideFromAnnouncement('banner-auto-dismiss');
  }, [eligibilityData, disableEligibility, openGuideFromAnnouncement]);

  const handleBannerAction = useCallback(
    (action: BannerActionSource) => {
      recordClick();
      disableEligibility();
      openGuideFromAnnouncement(action);
    },
    [recordClick, disableEligibility, openGuideFromAnnouncement]
  );

  const description = (
    <Trans t={t}>
      <strong>Summaries</strong> let you quickly catch up on every conversation. <strong>Full transcripts</strong>{' '}
      ensure you never miss a key detail.
    </Trans>
  );

  if (!shouldShow) return null;

  return (
    <AnnouncementBannerUI
      badgeLabel={t('New')}
      title={t('Keep track of every phone conversation with Summaries & Transcripts')}
      description={description}
      primaryActionLabel={t('Show Me Around')}
      onPrimaryAction={() => handleBannerAction('banner-click')}
      dismissActionLabel={t('Dismiss')}
      onDismissAction={() => handleBannerAction('banner-dismiss')}
      containerStyles={containerStyles}
    />
  );
}
